import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  Button,
  Checkbox,
  TableRow,
  CircularProgress,
  Tooltip,
  TextField,
  InputAdornment
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios";
import { baseUrl } from "../../Services/Constants";
import { processAndUpdateToken } from "../../Services/CommonFunctions";

const QuoteSelection = ({ brokerId, clientName, selectedQuotes, onSelectionChange, editSelection, loading, onBack, onNext }) => {
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterMode, setFilterMode] = useState("all");
  const hasAnySelection = Object.keys(selectedQuotes).length > 0;

  const totalSelectedCount = useMemo(() => {
    return Object.values(selectedQuotes).reduce((sum, item) => sum + (item.files?.length || 0), 0);
  }, [selectedQuotes]);

  const handleClearAll = () => {
    onSelectionChange({});
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const token = await processAndUpdateToken(sessionStorage.getItem("token"));
      const Broker = sessionStorage.getItem("proposalBrokerId");

      try {
        const url = `${baseUrl}/api/ProposalBuilder/GetClientJobs/${Broker}/GetJobs?clientName=${encodeURIComponent(clientName)}`;
        const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data?.success) {
          setTableData(res.data.data || []);
        }
      } catch (e) {
        console.error("API Error:", e);
      } finally {
        setIsLoading(false);
      }
    };
    if (brokerId && clientName) fetchData();
  }, [brokerId, clientName]);

  useEffect(() => {
    if (!hasAnySelection) {
      setError("");
    }
  }, [hasAnySelection]);

  const handleNext = () => {
    onNext();
  };

  const handleSelectAllRow = (row) => {
    const files = row.Files || [];
    if (files.length === 0) return;

    const updated = { ...selectedQuotes };
    const currentFiles = updated[row.JobCode]?.files || [];

    // If any files are already selected → clear all
    if (currentFiles.length > 0) {
      delete updated[row.JobCode];
    } else {
      // Select max 3 files
      updated[row.JobCode] = {
        JobCode: row.JobCode,
        JobId: row.JobId,
        LobId: row.LobId,
        BrokerId: brokerId,
        ClientName: clientName,
        lobName: lobDisplayMap[row.JobCode],
        files: files.slice(0, 3),
      };
    }

    onSelectionChange(updated);
  };

  const handleToggle = (JobCode, JobId, LobId, fileName) => {
    const updated = { ...selectedQuotes };
    const displayLobName = lobDisplayMap[JobCode];

    if (updated[JobCode]) {
      const files = updated[JobCode].files;

      if (files.includes(fileName)) {
        const newFiles = files.filter(f => f !== fileName);

        if (newFiles.length === 0) {
          delete updated[JobCode];
        } else {
          updated[JobCode] = {
            ...updated[JobCode],
            files: newFiles,
          };
        }
      } else {
        // MAX 3 FILE VALIDATION
        if (files.length >= 3) {
          setError("You can select maximum 3 quotes per LOB");
          return;
        }
        updated[JobCode] = {
          ...updated[JobCode],
          files: [...files, fileName],
        };

        setError("");
      }
    } else {
      updated[JobCode] = {
        JobCode,
        JobId,
        LobId,
        BrokerId: brokerId,
        ClientName: clientName,
        lobName: displayLobName,
        files: [fileName],
      };

      setError("");
    }

    onSelectionChange(updated);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";

    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-GB", { month: "short" });
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const lobDisplayMap = useMemo(() => {
    const countMap = {};
    const displayMap = {};

    tableData?.forEach((row) => {
      const lob = row.LobName;

      if (!countMap[lob]) {
        countMap[lob] = 0;
        displayMap[row.JobCode] = lob;
      } else {
        displayMap[row.JobCode] = `${lob} ${countMap[lob]}`;
      }

      countMap[lob]++;
    });

    return displayMap;
  }, [tableData]);

  const formatFileWithCarrier = (fileName, carrier) => {
    if (!carrier) return fileName;

    return `[${carrier}] ${fileName}`;
  };

  const isValidSelection = useMemo(() => {
    if (!tableData || tableData.length === 0) return false;

    return tableData.every((row) => {
      const totalFiles = row.Files?.length || 0;
      const selectedFiles = selectedQuotes[row.JobCode]?.files?.length || 0;

      // If row has 3 or fewer → always valid
      if (totalFiles <= 3) return true;

      // If more than 3 → must select at least 3
      return selectedFiles >= 3;
    });
  }, [tableData, selectedQuotes]);

  useEffect(() => {
    if (tableData.length === 0) return;
    // DO NOT override if user already selected something
    if (Object.keys(selectedQuotes).length > 0) return;

    const autoSelected = {};
    const updatedTableData = tableData.map((row) => {
      let updatedFiles = row.Files;
      if (row.Files && row.Files.length > 0) {
        const getCarrier = (fileName, carrierMap = {}) => {
          return (
            carrierMap[fileName] ||
            row.Carrier?.[fileName] ||
            ""
          );
        };

        if (!editSelection) {
          updatedFiles = row.Files.map(fileName => {
            const carrier = row.Carrier?.[fileName];

            return formatFileWithCarrier(fileName, carrier);
          });
        }

        // Edit mode → select only passed files
        if (editSelection?.files?.length) {
          const matchedEditFilesObj = editSelection?.files?.find(f => f?.JobCode === row?.JobCode);
          const selectedFiles = matchedEditFilesObj?.files || [];

          const selectedFileNames = selectedFiles.map(f => {
            const parts = f.split(".pdf");
            return parts[0]?.trim() + ".pdf";
          });
          const carrierMap = matchedEditFilesObj?.carrier || {};

          // const matchedFiles = row?.Files?.filter(f => selectedFiles?.includes(f));
          // const matchedFiles = row?.Files
          //   ?.filter(f => selectedFiles?.includes(f))
          //   ?.map(fileName => {
          //      const carrier = getCarrier(fileName, carrierMap);
          //     return carrier && carrier !== "Details not available in the document"
          //       ? `${fileName} - ${carrier}`
          //       : fileName;
          //   });
          const matchedFiles = row?.Files?.filter(f => selectedFileNames?.includes(f));

          if (matchedFiles.length > 0) {
            autoSelected[row.JobCode] = {
              JobCode: row.JobCode,
              JobId: row.JobId,
              LobId: row.LobId,
              BrokerId: brokerId,
              ClientName: clientName,
              lobName: lobDisplayMap[row.JobCode],
              files: matchedFiles?.map(fileName => {
                const carrier = getCarrier(fileName, carrierMap);
                return formatFileWithCarrier(fileName, carrier);
              }),
            };

            updatedFiles = row.Files.map(fileName => {
              const carrier = getCarrier(fileName, carrierMap);
              return formatFileWithCarrier(fileName, carrier);
            });
          }
        } else {
          // updatedFiles = row.Files.map(fileName => {
          //   const carrier = row.Carrier?.[fileName];

          //   return carrier && carrier !== "Details not available in the document"
          //     ? `${fileName} - ${carrier}`
          //     : fileName;
          // });

          // First time load → select ALL files
          autoSelected[row.JobCode] = {
            JobCode: row.JobCode,
            JobId: row.JobId,
            LobId: row.LobId,
            BrokerId: brokerId,
            ClientName: clientName,
            lobName: lobDisplayMap[row.JobCode],
            files: updatedFiles.slice(0, 3),
          };
        }
      }

      return {
        ...row,
        Files: updatedFiles,
      };
    });

    setTableData(updatedTableData);
    onSelectionChange(autoSelected);

  }, [tableData, editSelection])

  const maxQuoteCount = useMemo(() => {
    return Math.max(
      0,
      ...tableData.map(row => row.Files?.length || 0)
    );
  }, [tableData]);

  const baseWidth = 200 + 120;
  const quoteWidth = 220;

  const filteredTableData = useMemo(() => {

    const formatFileWithCarrierLocal = (file, carrierMap = {}) => {
      const carrier = carrierMap?.[file];
      return formatFileWithCarrier(file, carrier);
    };

    return tableData
      .map((row) => {
        const formattedFiles = (row.Files || []).map(file =>
          formatFileWithCarrierLocal(file, row.Carrier)
        );

        return {
          ...row,
          Files: formattedFiles
        };
      })
      .filter((row) => {
        const lobName = lobDisplayMap[row.JobCode] || "";

        const matchesSearch = lobName
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

        const hasSelection =
          (selectedQuotes[row.JobCode]?.files?.length || 0) > 0;

        const matchesFilter =
          filterMode === "all" ||
          (filterMode === "selected" && hasSelection);

        return matchesSearch && matchesFilter;
      });
  }, [tableData, searchTerm, filterMode, selectedQuotes, lobDisplayMap]);

  return (
    <Card variant="outlined" sx={{
      p: 3,
      borderRadius: "5px",
      border: "1px solid #e5e7eb",
      boxShadow: "none", margin: "15px 0px 0px 0px"
    }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, mt: -1 }}>
        <Typography sx={{ color: "#6b7280", fontSize: "11px" }}>
          A maximum of 3 quotes can be selected.
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search Lob..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              width: 200,
              "& .MuiInputBase-root": { fontSize: "12px", height: "32px", bgcolor: "white" }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: "18px", color: "#9ca3af" }} />
                </InputAdornment>
              ),
            }}
          />

          {/* 
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={filterMode}
              onChange={(e) => setFilterMode(e.target.value)}
              sx={{
                height: "32px",
                fontSize: "12px",
                bgcolor: "white",
                "& .MuiSelect-select": { py: 0 }
              }}
              displayEmpty
              startAdornment={
                <InputAdornment position="start">
                  <FilterListIcon sx={{ fontSize: "16px", color: "#9ca3af", ml: 1 }} />
                </InputAdornment>
              }
            >
              <MenuItem value="all" sx={{ fontSize: "12px" }}>All LOBs</MenuItem>
              <MenuItem value="selected" sx={{ fontSize: "12px" }}>Selected (LOBs) Only</MenuItem>
            </Select>
          </FormControl> */}

          <Box sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 0.5,
            bgcolor: totalSelectedCount > 0 ? "#eff6ff" : "#f3f4f6",
            borderRadius: "20px",
            border: "1px solid",
            borderColor: totalSelectedCount > 0 ? "#bfdbfe" : "#e5e7eb"
          }}>
            <Typography sx={{ fontSize: "12px", fontWeight: 600, color: totalSelectedCount > 0 ? "#2563eb" : "#9ca3af" }}>
              Selected Quotes: {totalSelectedCount}
            </Typography>
          </Box>

          <Button
            variant="text"
            size="small"
            onClick={handleClearAll}
            disabled={totalSelectedCount === 0}
            sx={{
              textTransform: "none",
              fontSize: "12px",
              fontWeight: 500,
              color: "#ef4444",
              "&:hover": { bgcolor: "#fef2f2" },
              "&.Mui-disabled": { color: "#d1d5db" }
            }}
          >
            Clear All
          </Button>
        </Box>
      </Box>

      {isLoading || loading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 10,
          }}

        >
          <CircularProgress size={40} sx={{ color: "#2563eb", mb: 2 }} />
          <Typography sx={{ color: "#6b7280" }}>
            {isLoading ? "Fetching quotes..." : "Fetching proposal sections..."}
          </Typography>
        </Box>
      ) : (

        <>
          <TableContainer
            sx={{
              maxHeight: "220px",
              overflowX: "auto",
              overflowY: "auto",
              border: "1px solid #f3f4f6",
              borderRadius: "8px",
            }}
          >
            <Table
              stickyHeader
              sx={{
                tableLayout: "fixed",
                minWidth: `${baseWidth + maxQuoteCount * quoteWidth}px`,
                // minWidth: maxQuoteCount > 5 ? 900 : "auto"
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      width: 200,
                      fontWeight: 700,
                      bgcolor: "#1e293b",
                      color: "#ffffff",
                      verticalAlign: "middle",
                      zIndex: 11,
                    }}
                  >
                    Line of Business
                  </TableCell>

                  <TableCell
                    sx={{
                      width: 120,
                      fontWeight: 700,
                      bgcolor: "#1e293b",
                      color: "#ffffff",
                      verticalAlign: "middle",
                      zIndex: 11,
                    }}
                  >
                    Date of Submission
                  </TableCell>

                  {Array.from({ length: maxQuoteCount }).map((_, i) => (
                    <TableCell
                      key={i}
                      sx={{
                        width: 220,
                        fontWeight: 700,
                        bgcolor: "#1e293b",
                        color: "#ffffff",
                        zIndex: 11,
                        verticalAlign: "middle",
                        minWidth: 180,
                      }}
                    >
                      Quote {i + 1}
                    </TableCell>
                  ))}

                </TableRow>
              </TableHead>

              <TableBody>
                {filteredTableData.map((row) => (
                  <TableRow key={row.JobCode} hover>

                    {/* LOB */}
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        verticalAlign: "middle",
                        py: 1.5,
                        width: 200,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {(() => {
                          const files = row.Files || [];
                          const selectedFiles = selectedQuotes[row.JobCode]?.files || [];
                          // const allChecked = files.length > 0 && files.every(f => selectedFiles.includes(f));
                          const allChecked = selectedFiles.length > 0;
                          const someChecked = files.some(f => selectedFiles.includes(f));
                          return (
                            <Tooltip title="Select all quotes for this row" arrow>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: allChecked || someChecked
                                    ? "0.1px solid #afc0e3"
                                    : "1px solid #e2e8f0",
                                  borderRadius: "6px",
                                  p: 0.5,
                                  backgroundColor: allChecked || someChecked
                                    ? "#eff6ff"
                                    : "#f9fafb",
                                  transition: "all 0.15s ease",
                                  "&:hover": {
                                    backgroundColor: "#f0f4ff",
                                    borderColor: "#93c5fd",
                                  },
                                }}
                              >
                                <Checkbox
                                  size="small"
                                  // checked={allChecked}
                                  checked={selectedFiles.length > 0}
                                  indeterminate={someChecked && !allChecked}
                                  onChange={() => handleSelectAllRow(row)}
                                  sx={{
                                    padding: "0px",
                                    color: "#93c5fd",
                                    "& .MuiSvgIcon-root": { fontSize: "15px" },
                                    "&.Mui-checked": { color: "#2563eb" },
                                    "&.MuiCheckbox-indeterminate": { color: "#2563eb" },
                                  }}
                                />
                              </Box>
                            </Tooltip>
                          );
                        })()}
                        <Typography sx={{ fontWeight: 600, fontSize: "10px", color: "#111827", wordBreak: "break-word" }}>
                          {lobDisplayMap[row.JobCode]}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Date */}
                    <TableCell
                      sx={{
                        color: "#2563eb",
                        verticalAlign: "middle",
                        py: 1.5,
                      }}
                    >
                      {formatDate(row.SubmissionDate)}
                    </TableCell>

                    {/* Quote Columns */}
                    {Array.from({ length: maxQuoteCount }).map((_, i) => {
                      const file = row.Files?.[i];

                      return (
                        <TableCell
                          key={i}
                          sx={{
                            minWidth: 180,
                            verticalAlign: "center",
                            pt: "5px",
                            pb: "8px",
                          }}
                        >
                          {file ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 1,
                                border: selectedQuotes[row.JobCode]?.files?.includes(file)
                                  ? "1px solid #afc0e3"
                                  : "1px solid #e2e8f0",
                                borderRadius: "8px",
                                px: 1.2,
                                py: 0.8,
                                backgroundColor: selectedQuotes[row.JobCode]?.files?.includes(file)
                                  ? "#eff6ff"
                                  : "#f9fafb",
                                transition: "all 0.15s ease",

                                height: "100%",
                                boxSizing: "border-box",

                                "&:hover": {
                                  backgroundColor: "#f0f4ff",
                                  borderColor: "#93c5fd",
                                },
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={
                                  selectedQuotes[row.JobCode]?.files?.includes(file) ||
                                  false
                                }
                                disabled={
                                  !selectedQuotes[row.JobCode]?.files?.includes(file) &&
                                  (selectedQuotes[row.JobCode]?.files?.length || 0) >= 3
                                }
                                onChange={() =>
                                  handleToggle(
                                    row.JobCode,
                                    row.JobId,
                                    row.LobId,
                                    file
                                  )
                                }
                                sx={{
                                  padding: "0px",
                                  color: "#93c5fd",
                                  "& .MuiSvgIcon-root": { fontSize: "15px" },
                                  "&.Mui-checked": { color: "#2563eb" },
                                }}
                              />

                              {i === 0 ? (
                                <Tooltip title="Base Document" arrow>
                                  <Typography
                                    sx={{
                                      fontSize: "9px",
                                      fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                                      whiteSpace: "normal",
                                      wordBreak: "break-word",
                                      overflowWrap: "anywhere",
                                      lineHeight: 1.3,
                                      color: "#1e293b",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {file}
                                    <Box component="span" sx={{ color: "#dc2626", margin: "0px 0px 0px 3px", fontWeight: "bolder", fontSize: "10px" }}>
                                      *
                                    </Box>
                                  </Typography>
                                </Tooltip>
                              ) : (
                                <Typography
                                  sx={{
                                    fontSize: "9px",
                                    fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                    overflowWrap: "anywhere",
                                    lineHeight: 1.3,
                                    color: "#1e293b",
                                    fontWeight: 500,
                                  }}
                                >
                                  {file}
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography sx={{ color: "#9ca3af" }}>
                              —
                            </Typography>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>


          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <Button variant="outlined" onClick={onBack} sx={{
              textTransform: "none",
              pl: "22px",
              pr: "24px",
              pt: 0,
              pb: 0,
              borderRadius: "5px",
              height: "35px",
              fontSize: "11.5px",
              fontWeight: 500,
              border: "1px solid #e5e7eb",
              // backgroundColor: "#f9fafb",
              color: "#1f2937",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "white",
              },
              "&.Mui-disabled": {
                backgroundColor: "#000000f5",
                color: "#000000f5",
              },
            }}>Back</Button>

            <Button variant="contained" disabled={!isValidSelection} onClick={handleNext} sx={{
              textTransform: "none",
              pl: "22px",
              pr: "24px",
              pt: 0,
              pb: 0,
              borderRadius: "5px",
              height: "35px",
              fontSize: "11.5px",
              fontWeight: 500,
              backgroundColor: "#2563eb",
              color: "white",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#2563eb",
              },
              "&.Mui-disabled": {
                backgroundColor: "#e5e7eb",
                color: "#9ca3af",
              },
            }}>Next</Button>
            {error && (
              <Typography
                sx={{
                  color: "error.main",
                  fontSize: "10px",
                  ml: 1,
                  mt: 1,
                  whiteSpace: "nowrap",
                }}
              >
                {error}
              </Typography>
            )}
          </Box>
        </>
      )}
    </Card>
  );
};

export default QuoteSelection;